import { useContext, useEffect } from 'react';

import SeasonalCard from '@/components/game/ui/card/SeasonalCard';
import SummitCard from '@/components/game/ui/card/SummitCard';
import { createPayment } from '@/lyoc-common/src/interface/payment/factory';
import { GameContext, ViewContext } from '@/state/contexts';
import { getCardMetadata, cardPurchaseRequiresDecision } from '@/util/card';
import { logg } from '@/util/logg';
import { SummitMeta } from '@lc/card/meta/SummitMeta';
import {
	factoryHalfRoundedDown,
	factoryHalfRoundedUp,
	summitTypeTurn as turn,
} from '@lc/const/modifiers';
import { playCard, playSummitCard } from '@lc/const/socketEvent';
import '@/style/HandModal.css';
import { createActionParams } from '@lc/interface/actionParams/factory';
import { calculateFactoryCost } from '@lc/lib/playermat_helper';

/**
 * Used for any actions regarding viewing/playing cards from your hands.
 *
 * Includes Seasonal Cards and Summit Cards
 *
 *
 * Ignoring Summit recycling for now because it's a lot of work for 1 card
 *
 * TODO break down decision cards in to choice that has to be made
 * Choose Card
 * Choose player
 * Choose outcome or shortcut for easy choices
 * Start thinking about V15 cards, light card rewrite to fit code decision overwrites
 */
const HandModal = ({
	onClose,
	requestClientPayment,
	requestClientDecision,
	// seasonalCards,
	// summitCards,
	resetPaymentObject,
}) => {
	const {
		// currPlayer,
		myState,

		monthTracker,
		setSelectedAction,
		setSelectedActionCost,
		setSelectedCard,
		setSelectedCardId,

		setPaymentTotal,
		setPaymentObject,

		performAction,

		isTutorial,
		stepTutorial,
		setStepTutorial
	} = useContext(GameContext);

	const {
		setPaymentMode,
		currentTickerTimer,
		setCurrentTickerIndex
	} = useContext(ViewContext);

	useEffect(() => {
		setSelectedAction('');
		resetPaymentObject();
	}, []);

	function getCardCost(card) {
		if (card.type === 'Award') {
			return card.cost[monthTracker];
		}
		return card.cost;
	}

	function logHandleCardPlay(actionType, cardId, card, cost, makeDecision) {
		logg('logHandleCardPlay');
		logg('cardId', cardId);
		logg('card', card);
		logg('actionType', actionType);
		logg('cost', cost);
		logg('makeDecision', makeDecision);
	}

	function resetPaymentParams() {
		setPaymentMode(false);
		const emptyPayment = createPayment();
		setPaymentObject(emptyPayment);
		setPaymentTotal(0);
		setSelectedAction('');
		setSelectedActionCost(0);
	}

	// if choice here, if payment cost is 0, just play the card via socket call
	// remember, summit cards are basically 1 time events if playable
	// and most don't have costs
	function handleCardPlay(
		actionType,
		cardId,
		card,
		cost = 0,
		makeDecision = false
	) {
		resetPaymentParams();
		logHandleCardPlay(actionType, cardId, card, cost, makeDecision);

		setSelectedAction(actionType);
		setSelectedActionCost(cost);
		setSelectedCard(card);
		setSelectedCardId(cardId);

		if (cost > 0) {
			setPaymentMode(true);
			onClose();
			return;
		}

		if (makeDecision) {
			requestClientDecision();
			return;
		}

		const actionParams = createActionParams(cardId);

		// card is free and does not require decision. Play immediately
		performAction(actionType, actionParams);
		onClose();
	}

	function playCardFunc(e, cardId) {
		e.preventDefault();

		if(isTutorial && stepTutorial === 22){
			setStepTutorial((prev) => prev + 1);
			clearTimeout(currentTickerTimer);
			setCurrentTickerIndex((prev) => prev + 1);
		}

		if(isTutorial && stepTutorial === 26){
			setStepTutorial((prev) => prev + 1);
			clearTimeout(currentTickerTimer);
			setCurrentTickerIndex((prev) => prev + 1);

			onClose();
			return;
		}

		const actionType = playCard;
		const card = getCardMetadata(cardId);
		const makeDecision = cardPurchaseRequiresDecision(cardId);
		const cost = getCardCost(card);

		handleCardPlay(actionType, cardId, card, cost, makeDecision);
	}

	function playSummitCardFunc(e, cardId) {
		e.preventDefault();
		const actionType = playSummitCard;
		const card = SummitMeta[cardId];
		let cost = 0;
		let makeDecision = false; // TODO makeDecision for summit cards

		// This is basically just for summit cards that buy factories
		const factoryCost = calculateFactoryCost(myState);

		if (card.modifier === factoryHalfRoundedUp) {
			cost = Math.ceil(factoryCost / 2);
		}

		if (card.modifier === factoryHalfRoundedDown) {
			cost = Math.floor(factoryCost / 2);
		}

		handleCardPlay(actionType, cardId, card, cost, makeDecision);
	}

	// <p>Type: {card.type} - Title: {card.title} - Description: {card.description} - Cost: {card.cost}</p>
	logg('seasonalCards');
	logg(myState?.seasonalCards);

	return (
		<div className="modal" onClick={onClose}>
			<div
				className="modal-content-hand"
				onClick={(e) => e.stopPropagation()}
			>
				<span className="close" onClick={onClose}>
					&times;
				</span>
				<div className="flexCol">
					<h2 className="modal-title">Seasonal Cards</h2>
					<div className="hand">
						{myState?.seasonalCards.map((cardId) => (
							<div className="card">
								{/*logg(cardId)*/}
								<SeasonalCard
									cardId={cardId}
									width={0}
									height={0}
								/>
								<button
									value={0}
									onClick={(e) => playCardFunc(e, cardId)}
								>
									Play Card
								</button>
							</div>
						))}
					</div>
				</div>
				<div className="flexColSummit">
					<h2 className="modal-title">Summit Cards</h2>
					{myState?.summitCards.length > 0 && (
						<div className="hand">
							{myState?.summitCards.map((cardId) => (
								<div className="card">
									{logg(cardId)}
									<SummitCard
										cardId={cardId}
										width={0}
										height={0}
									/>
									{SummitMeta[cardId].type === turn && (
										<button
											value={0}
											onClick={(e) =>
												playSummitCardFunc(e, cardId)
											}
										>
											Play Card
										</button>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default HandModal;
